import React from "react";
import { MetricCard } from "../../../../common/comms/MetricCard";
import { formatDuration, formatSecondsBrief, pct } from "../../../../utils/oversightFormat";
import { CoachTrendCharts } from "../CoachTrendCharts";
import { TodayStrip } from "./TodayStrip";
import { ChatEngagementCard } from "./ChatEngagementCard";
import { ChatVolumeChart } from "./ChatVolumeChart";
import { ContactCadence } from "./ContactCadence";
import { SupportDeskCard } from "./SupportDeskCard";

/**
 * The secondary metrics that are real but not the primary signal: quality,
 * escalation and AI assistance. Kept as a compact row under the main panel so
 * the Overview leads with engagement rather than with a wall of tiles.
 */
function SecondaryMetrics({ totals }) {
  if (!totals) return null;

  const csat =
    totals.csat_avg != null && Number.isFinite(Number(totals.csat_avg))
      ? Number(totals.csat_avg).toFixed(1)
      : "—";
  const csatCount = totals.csat_count ?? 0;
  const crisis = totals.crisis_count ?? null;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <MetricCard
        label="CSAT"
        value={csat}
        sub={csatCount > 0 ? `${csatCount} rating${csatCount === 1 ? "" : "s"}` : undefined}
      />
      <MetricCard
        label="Avg read latency"
        value={formatSecondsBrief(totals.avg_read_latency_seconds)}
        sub="how long before they open a message"
      />
      <MetricCard
        label="Crisis flags"
        value={crisis == null ? "—" : crisis}
        tone={crisis > 0 ? "danger" : "neutral"}
      />
      <MetricCard
        label="AI interventions"
        value={totals.ai_interventions ?? "—"}
        sub="replies the assistant handled"
      />
    </div>
  );
}

/**
 * The Plans summary band on the Overview tab: a pointer at the Plans tab plus
 * the headline it will carry.
 *
 * Empty for the same reason the Plans tab is: plan authoring is recorded in
 * `ChatSchema.PlanEvents`, which nothing reads yet.
 */
function PlansBand({ onOpenPlans }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex flex-wrap items-baseline gap-2.5 mb-4">
        <h2 className="text-base font-bold text-gray-900">Plans</h2>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-coach-2-bg text-ca-plans">
          Second emphasis
        </span>
        <button
          type="button"
          onClick={onOpenPlans}
          className="sm:ml-auto min-h-[36px] text-xs font-semibold text-orange-primary hover:text-orange-600"
        >
          Open Plans tab →
        </button>
      </div>
      <p className="text-xs text-gray-500">
        Plans created, who received them, how often they change and which ones are worth a second
        look. Every one of these comes from the plan-events stream, which is being written but not
        yet read.
      </p>
    </div>
  );
}

/**
 * Overview — "is this coach engaged?"
 *
 * Leads with the live strip and the chat-engagement panel (the primary
 * signal), then the roster's contact cadence, then the secondary quality
 * metrics, with Support and Plans held to the side as clearly-labelled
 * secondary and second-emphasis surfaces.
 *
 * @param {Object} props
 * @param {Object|null|undefined} props.payload - The coach analytics response.
 * @param {'day'|'week'} props.granularity
 * @param {Array<Object>} props.clients - Roster rows for the cadence panel.
 * @param {boolean} props.clientsLoading
 * @param {() => void} props.onOpenPlans
 * @param {{totals: Object|null, loading: boolean, errorKind: string|null}} props.support -
 *   Support-desk metrics from the shell-owned `useCoachSupportMetrics`.
 */
// The four trend charts are kept from v1. The v2 design replaces them with the
// heatmap and the plan panels, neither of which has a data source yet, so
// dropping them now would take working analytics away and put nothing back.
export function OverviewTab({
  payload,
  granularity,
  clients,
  clientsLoading,
  onOpenPlans,
  support,
}) {
  const totals = payload?.totals;
  // The roster is the only authoritative client count; `totals.total_rooms`
  // counts rooms of every type the coach currently sits in. Held back while the
  // roster is still loading so the panel never renders a wrong denominator.
  const assignedClients = clientsLoading ? null : (clients?.length ?? null);

  return (
    <div className="flex flex-col gap-4">
      <TodayStrip payload={payload} />

      <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-4 items-start">
        <div className="flex flex-col gap-4 min-w-0">
          <ChatEngagementCard
            payload={payload}
            assignedClients={assignedClients}
            chart={
              <ChatVolumeChart
                series={payload?.series}
                granularity={granularity}
                totalRooms={totals?.total_rooms}
              />
            }
          />
          <ContactCadence clients={clients} loading={clientsLoading} />
        </div>

        <SupportDeskCard
          totals={support?.totals ?? null}
          loading={support?.loading ?? false}
          errorKind={support?.errorKind ?? null}
        />
      </div>

      <SecondaryMetrics totals={totals} />

      <div>
        <h2 className="mb-3 text-base font-bold text-gray-900">Trends</h2>
        <CoachTrendCharts series={payload?.series} granularity={granularity} />
      </div>

      <PlansBand onOpenPlans={onOpenPlans} />

      <p className="text-[11px] text-gray-400">
        Online time this period: {formatDuration(totals?.online_seconds)} ·{" "}
        {totals?.sessions_count ?? "—"} sign-ins · SLA{" "}
        {totals?.sla_hit_rate != null ? pct(totals.sla_hit_rate) : "—"}
      </p>
    </div>
  );
}

export default OverviewTab;
